import { z } from "zod";
import {
  CustomFieldsSchemaSchema,
  CustomFieldValuesSchema,
} from "./customFields";
import { ExternalMappingSchema } from "./externalMappings";
import {
  ISO_3166_1_ALPHA_2_CODES,
  isISO31661Alpha2Code,
} from "../constants/countryCodes";

export const IsoCountryCodeSchema = z
  .string()
  .trim()
  .length(2, { message: "Country code must be exactly 2 characters" })
  .transform((value) => value.toUpperCase())
  .pipe(z.enum(ISO_3166_1_ALPHA_2_CODES))
  .describe("ISO-3166-1 alpha-2 country code");

export type IsoCountryCode = z.infer<typeof IsoCountryCodeSchema>;

export const IsoCountryCodeSchemaSimple = z
  .string()
  .trim()
  .length(2, { message: "Country code must be exactly 2 characters" })
  .refine(isISO31661Alpha2Code, {
    message: "Invalid ISO-3166-1 alpha-2 country code",
  })
  .describe("ISO-3166-1 alpha-2 country code");

export type IsoCountryCodeSimple = z.infer<typeof IsoCountryCodeSchemaSimple>;

const NullableAddressStringSchema = z.string().nullable().default(null);

// Address schema (shared across multiple entities)
export const AddressSchema = z.preprocess(
  (value) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return value;
    }

    const address = value as Record<string, unknown>;
    return {
      ...address,
      name: address.name ?? null,
      address_line_1:
        address.address_line_1 ??
        address.addressLine1 ??
        address.street ??
        null,
      address_line_2: address.address_line_2 ?? address.addressLine2 ?? null,
      city: address.city ?? null,
      zipcode: address.zipcode ?? address.postalCode ?? address.zip ?? null,
      state: address.state ?? null,
      country: address.country ?? null,
    };
  },
  z.object({
    name: NullableAddressStringSchema,
    address_line_1: NullableAddressStringSchema,
    address_line_2: NullableAddressStringSchema,
    city: NullableAddressStringSchema,
    zipcode: NullableAddressStringSchema,
    state: NullableAddressStringSchema,
    country: IsoCountryCodeSchemaSimple.nullable().default(null),
  }),
);

export type Address = z.infer<typeof AddressSchema>;

// Core Company schema
export const CompanySchema = z.object({
  id: z.string(),
  name: z.string(),
  address: AddressSchema.nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  salesRepUserId: z.string().nullable().optional(),
  customFields: CustomFieldValuesSchema.optional(),
  organizationId: z.string(),
  externalMappings: z.array(ExternalMappingSchema).default([]),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export type Company = z.infer<typeof CompanySchema>;

/** Minimal primary-contact embed carried on hydrated companies. */
export const CompanyPrimaryContactSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string().nullable(),
});

export type CompanyPrimaryContact = z.infer<typeof CompanyPrimaryContactSchema>;

// Hydrated Company schema (includes primary contact object)
export const HydratedCompanySchema = CompanySchema.extend({
  primaryContact: CompanyPrimaryContactSchema.nullable().optional(),
});
export type HydratedCompany = z.infer<typeof HydratedCompanySchema>;

// Request/response schemas for API endpoints
export const CreateCompanyDataSchema = z.object({
  name: z.string(),
  address: AddressSchema.optional(),
  phone: z.string().optional(),
  email: z.string().optional(),
  salesRepUserId: z.string().nullable().optional(),
  customFields: CustomFieldValuesSchema.optional(),
});

export type CreateCompanyData = z.infer<typeof CreateCompanyDataSchema>;

export const UpdateCompanyDataSchema = z.object({
  name: z.string().optional(),
  address: AddressSchema.optional(),
  phone: z.string().optional(),
  email: z.string().optional(),
  salesRepUserId: z.string().nullable().optional(),
  customFields: CustomFieldValuesSchema.optional(),
});

export type UpdateCompanyData = z.infer<typeof UpdateCompanyDataSchema>;

export const CompanySettingsSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  customFieldsSchema: CustomFieldsSchemaSchema.nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type CompanySettings = z.infer<typeof CompanySettingsSchema>;

export const UpdateCompanySettingsParamsSchema = z.object({
  customFieldsSchema: CustomFieldsSchemaSchema.nullable(),
});
export type UpdateCompanySettingsParams = z.infer<
  typeof UpdateCompanySettingsParamsSchema
>;

export const CompanySettingsResponseSchema = z.object({
  settings: CompanySettingsSchema.nullable(),
});
export type CompanySettingsResponse = z.infer<
  typeof CompanySettingsResponseSchema
>;

export const CompanyFilterSchema = z.object({
  search: z.string().optional(),
  limit: z.number().int().positive().optional(),
  offset: z.number().int().nonnegative().optional(),
});

export type CompanyFilter = z.infer<typeof CompanyFilterSchema>;

// API Response schemas
export const GetCompaniesResponseSchema = z.object({
  companies: z.array(HydratedCompanySchema),
  total: z.number(),
});

export type GetCompaniesResponse = z.infer<typeof GetCompaniesResponseSchema>;

export const GetCompanyResponseSchema = z.object({
  company: HydratedCompanySchema,
});

export type GetCompanyResponse = z.infer<typeof GetCompanyResponseSchema>;

export const CreateCompanyResponseSchema = z.object({
  company: HydratedCompanySchema,
});

export type CreateCompanyResponse = z.infer<typeof CreateCompanyResponseSchema>;

export const UpdateCompanyResponseSchema = z.object({
  company: HydratedCompanySchema,
});

export type UpdateCompanyResponse = z.infer<typeof UpdateCompanyResponseSchema>;

export const DeleteCompanyResponseSchema = z.object({
  success: z.boolean(),
});

export type DeleteCompanyResponse = z.infer<typeof DeleteCompanyResponseSchema>;

// Error response schema (shared across endpoints)
export const CompanyErrorResponseSchema = z.object({
  error: z.string(),
});

export type CompanyErrorResponse = z.infer<typeof CompanyErrorResponseSchema>;
